import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";
import Modal from "@via/components/modal";
import { requiredValidation } from "@via/utils/form-validators";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useProcessIsUsed } from "@via/pages/projects/pipelines/pipelines.api";
import { USER_ROLES } from "@via/utils/constants";
import { useAuth } from "@via/hooks/useAuth";

const isUsedByUserMessage =
  "Process is used only in your runs. So you can either save your changes on same version or create a new revision.";
const isUsedByOthersAdminMessage = "Process is in use by others.";
const isUsedByOthersUserMessage =
  "Process is in use by others. You can save your changes by creating a new revision.";

export const ModalType = {
  CREATE_REVISION: "createRevision",
  OVERWRITE_REVISION: "overwriteRevision",
};

export default function RevisionCommentModal({
  isLoading,
  type,
  setOpen,
  draft,
  onConfirm,
}) {
  const { user } = useAuth();
  const {
    data: processIsUsedData,
    isLoading: isProcessIsUsedLoading,
    isSuccess: isProcessIsUsedSuccess,
  } = useProcessIsUsed(
    type === ModalType.OVERWRITE_REVISION ? draft?.processId : null
  );

  const [message, showMessage] = useState("");

  useEffect(() => {
    if (isProcessIsUsedSuccess && processIsUsedData) {
      const { usedByRequesterUser, usedByOtherUsers } = processIsUsedData;
      if (usedByRequesterUser) {
        showMessage(isUsedByUserMessage);
      } else if (usedByOtherUsers && user.userRole === USER_ROLES.ADMIN) {
        showMessage(isUsedByOthersAdminMessage);
      } else if (usedByOtherUsers && user.userRole === USER_ROLES.USER) {
        showMessage(isUsedByOthersUserMessage);
      } else {
        showMessage("");
      }
    }
  }, [processIsUsedData, isProcessIsUsedSuccess]);

  const formRef = useRef<HTMLFormElement>(null);

  const { control, handleSubmit } = useForm<{
    revisionComment: string;
  }>({
    mode: "onChange",
    defaultValues: {
      revisionComment:
        type === ModalType.OVERWRITE_REVISION ? draft?.revisionComment : "",
    },
  });

  const onSubmit = handleSubmit(async (data, event) => {
    if (!event) return;

    const submitter = (event.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement | null;
    if (!submitter) return;

    const modalType = submitter.name;

    await onConfirm(data.revisionComment, modalType);
  });

  return (
    <Modal
      open={!!type}
      setOpen={setOpen}
      title="Revision Comment"
      hasCancelButton={false}
      isConfirmLoading={isLoading || isProcessIsUsedLoading}>
      <div className="my-4 max-w-[650px]">
        <span className="text-sm text-gray-600">{message}</span>
        <form
          ref={formRef}
          onSubmit={e => {
            e.preventDefault();
            onSubmit(e);
          }}>
          <FormTextAreaComponent
            name="revisionComment"
            placeholder="Enter revision comment"
            showRequiredField={true}
            control={control}
            validations={requiredValidation("Revision Comment")}
          />
          <div className="flex gap-2 justify-end mt-4">
            {type === ModalType.OVERWRITE_REVISION && (
              <>
                {!processIsUsedData?.usedByRequesterUser &&
                  !processIsUsedData?.usedByOtherUsers && (
                    <button
                      className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                      type="submit"
                      name={ModalType.OVERWRITE_REVISION}>
                      Overwrite Revision
                    </button>
                  )}
                {processIsUsedData?.usedByRequesterUser &&
                  !processIsUsedData?.usedByOtherUsers && (
                    <>
                      <button
                        className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-yellow-600 hover:bg-yellow-500 focus-visible:outline-yellow-600"
                        type="submit"
                        name={ModalType.OVERWRITE_REVISION}>
                        Overwrite Revision
                      </button>
                      <button
                        className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                        type="submit"
                        name={ModalType.CREATE_REVISION}>
                        Create Revision
                      </button>
                    </>
                  )}
                {processIsUsedData?.usedByOtherUsers && (
                  <>
                    {user?.userRole === USER_ROLES.ADMIN && (
                      <button
                        className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-red-600 hover:bg-red-500 focus-visible:outline-red-600"
                        type="submit"
                        name={ModalType.OVERWRITE_REVISION}>
                        Overwrite Revision
                      </button>
                    )}
                    <button
                      className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                      type="submit"
                      name={ModalType.CREATE_REVISION}>
                      Create Revision
                    </button>
                  </>
                )}
              </>
            )}
            {type === ModalType.CREATE_REVISION && (
              <button
                className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                type="submit"
                name={ModalType.CREATE_REVISION}>
                Create Revision
              </button>
            )}
            <button
              className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-gray-600 hover:bg-gray-500 focus-visible:outline-gray-600"
              type="button"
              onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
